import { AudienceType } from '@enums';
import type { Dataset as DatasetType } from 'types/schema';
import { z } from 'zod';

export const ApiError = z.object({
  code: z.number(),
  type: z.string().optional(),
  error: z.string(),
  message: z.string(),
  extra: z.record(z.any()).optional()
});

export const AttributeKey = z.object({
  namespace_code: z.string(),
  question_code: z.string(),
  datapoint_code: z.string(),
  suffix_code: z.string().optional()
});

export const AttributeWithText = z.object({
  attribute_key: AttributeKey.optional(),
  generated_text: z.string().optional()
});

export const Audience = z.object({
  id: z.string().optional(),
  title: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  sample: z.number().optional(),
  universe: z.number().optional(),
  percentage: z.number().optional(),
  datasets: z.array(z.string()).optional(),
  is_origin: z.boolean().optional(),
  is_outdated: z.boolean().optional(),
  type: z.nativeEnum(AudienceType).optional()
});

export const InsightOrigins = z.object({
  attributes: z.array(AttributeWithText).optional(),
  audiences: z.array(Audience).optional()
});

export const Insight = z.object({
  id: z.string(),
  text: z.string(),
  is_pinned: z.boolean().optional(),
  is_disliked: z.boolean().optional(),
  origins: InsightOrigins.optional(),
  has_real_access: z.boolean().optional()
});

export const InsightsSummarization = z.object({
  id: z.string(),
  text: z.string(),
  regenerate_allowed: z.boolean().optional(),
  created_at: z.string()
});

export const ChatAuthRequest = z.object({
  token: z.string()
});

export const ChatRequest = z.object({
  chat_id: z.string()
});

export const ChatPromptRequest = z.object({
  prompt: z.string(),
  message_type: z.string(),
  agent_version: z.string(),
  agent: z.string().optional(),
  docked_audiences: z.array(z.string()).optional(),
  dataset_codes: z.array(z.string()).optional(),
  dataset_code: z.string().optional()
});

export const ChatResponseMetadata = z.object({
  response_message_type: z.string(),
  chat_id: z.string(),
  message_id: z.string(),
  trace_id: z.string(),
  created_at: z.string(),
  // Not used in the API, but useful for client-side logic
  from_saved_chat: z.boolean().optional()
});

export const ChatUserResponse = z.object({
  metadata: ChatResponseMetadata,
  message: z.string()
});

export const ChatAudiencesResponse = z.object({
  metadata: ChatResponseMetadata,
  natural_language_response: z.string(),
  audiences: z.array(Audience).optional(),
  failed_audiences: z.optional(
    z.array(
      z.object({
        audience_text: z.string(),
        failure_reason: ApiError.optional()
      })
    )
  ),
  error: ApiError.optional()
});

export const ChatListItem = z.object({
  id: z.string(),
  trace_id: z.string(),
  title: z.string(),
  created_at: z.string(),
  last_message_created_at: z.string().optional()
});

export const ChatListItemGroup = z.object({
  title: z.string(),
  chats: z.array(ChatListItem).optional()
});

export const ChatListResponse = z.object({
  chat_groups: z.array(ChatListItemGroup)
});

// Not used
export const EditChatTitleRequest = z.object({
  title: z.string()
});

export const EditChatTitleResponse = z.object({
  error: ApiError.optional()
});

// Not used
export const DeleteChatResponse = z.object({
  error: ApiError.optional()
});

export const GenerateChatTitleResponse = z.object({
  title: z.string(),
  error: ApiError.optional()
});

export const AgentVersion = z.object({
  key: z.string(),
  display_name: z.string()
});

export const AgentVersionsResponse = z.array(AgentVersion);

export const ChatSourcesLocation = z.object({
  id: z.string(),
  name: z.string()
});

export const ChatSourcesLocationDetails = z.object({
  description: z.string(),
  locations: z.array(ChatSourcesLocation).optional()
});

export const ChatSourcesLocationGroup = z.object({
  group: z.string(),
  details: ChatSourcesLocationDetails
});

export const ChatSourcesTimeframe = z.object({
  id: z.string(),
  name: z.string()
});

export const ChatSourcesTimeframeDetails = z.object({
  description: z.string(),
  timeframes: z.array(ChatSourcesTimeframe)
});

export const ChatSourcesTimeframeGroup = z.object({
  group: z.string(),
  details: ChatSourcesTimeframeDetails
});

// Validates against the hand-written `Dataset` interface in `types/schema.ts`
// rather than letting Zod infer the type, so the inferred type stays strict
// even with tsconfig `strict: false`.
export const Dataset = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  url: z.string().optional()
}) as z.ZodType<DatasetType>;

// z.lazy() can't infer recursive types — cast via local shapes to avoid `any`.
type DatasetNodeShape = {
  dataset_node: {
    dataset: DatasetType;
    is_selected: boolean;
  };
  children: (DatasetNodeShape | DatasetFolderNodeShape)[];
};

type DatasetFolderNodeShape = {
  dataset_folder_node: {
    id: string;
    name: string;
    is_selected: boolean;
    children: (DatasetNodeShape | DatasetFolderNodeShape)[];
  };
};

export const DatasetNode = z.lazy(() =>
  z.object({
    dataset_node: z.object({
      dataset: Dataset,
      is_selected: z.boolean()
    }),
    // The backend omits `children` for leaf datasets, but the picker reads
    // `node.children` unconditionally — default to [] so the parsed shape
    // always satisfies the picker's contract.
    children: z.array(z.union([DatasetNode, DatasetFolderNode])).default([])
  })
) as z.ZodType<DatasetNodeShape>;

export const DatasetFolderNode = z.lazy(() =>
  z.object({
    dataset_folder_node: z.object({
      id: z.string(),
      name: z.string(),
      is_selected: z.boolean(),
      children: z.array(z.union([DatasetNode, DatasetFolderNode]))
    })
  })
) as z.ZodType<DatasetFolderNodeShape>;

type DatasetTreeShape = {
  children: (DatasetNodeShape | DatasetFolderNodeShape)[];
};

export const DatasetTree = z.object({
  children: z.array(z.union([DatasetNode, DatasetFolderNode]))
}) as z.ZodType<DatasetTreeShape>;

export const ChatSourcesDatasetResponse = z.object({
  datasets: z.array(Dataset)
});

export const SourcesSummaryResponse = z.object({
  summary: z.string(),
  error: ApiError.optional()
});

export const ChatNaturalLanguageResponse = z.object({
  metadata: ChatResponseMetadata,
  natural_language_response: z.string(),
  sources_available: z.boolean()
});

export const ChatLoadingResponse = ChatNaturalLanguageResponse;

export const PromptUsage = z.object({
  usage_remaining: z.number().optional(),
  usage_total: z.number(),
  reset_at: z.string()
});

export const ChatPromptUsageResponse = z
  .object({
    metadata: ChatResponseMetadata
  })
  .merge(PromptUsage);

export const Message = z.discriminatedUnion('message_type', [
  z.object({
    message_type: z.literal('user'),
    message: ChatUserResponse
  }),
  z.object({
    message_type: z.literal('assistant_loading'),
    message: ChatLoadingResponse
  }),
  z.object({
    message_type: z.literal('assistant_prompt_usage'),
    message: ChatPromptUsageResponse
  }),
  z.object({
    message_type: z.literal('assistant_natural_language'),
    message: ChatNaturalLanguageResponse
  })
]);

export const Chat = z.object({
  id: z.string(),
  title: z.string(),
  created_at: z.string(),
  last_message_created_at: z.string().optional(),
  messages: z.array(Message),
  dataset: Dataset.optional()
});

export const QuestionKey = z.object({
  namespace_code: z.string(),
  question_code: z.string()
});

export const Question = z.object({
  question_key: QuestionKey,
  question_description: z.string().optional(),
  question_name: z.string().optional()
});

export const Attribute = z.object({
  attribute_key: AttributeKey,
  question_description: z.string().optional(),
  question_name: z.string().optional(),
  datapoint_name: z.string().optional(),
  suffix_name: z.string().optional(),
  generated_text: z.string().optional(),
  is_origin: z.boolean().optional()
});

export const InsightAttributeCalculations = z.object({
  attribute: Attribute,
  audience_id: z.string().optional(),
  base_audience_label: z.string().optional(),
  percentage: z.number().optional(),
  index: z.number().optional(),
  sample: z.number().optional(),
  size: z.number().optional()
});

export type InsightAttributeCalculations = z.infer<
  typeof InsightAttributeCalculations
>;

export const InsightQuestionCalculations = z.object({
  question: Question,
  suffixes: z.string().optional(),
  metric_priority: z.array(z.string()),
  calculations: z.array(InsightAttributeCalculations)
});

export const SourcesOverviewResponse = z.object({
  topics: z.array(z.string()),
  audiences: z.array(Audience),
  attribute_codes: z.array(AttributeKey),
  namespaces: z.array(z.string()),
  questions: z.array(Question),
  location_groups: z.array(ChatSourcesLocationGroup),
  timeframe_groups: z.array(ChatSourcesTimeframeGroup),
  datasets: z.array(Dataset),
  error: ApiError.optional()
});

export const InsightChart = z.object({
  attributes: z.array(Attribute).optional(),
  audiences: z.array(Audience).optional(),
  datasets: z.array(Dataset),
  has_real_access: z.boolean().optional(),
  insight: Insight,
  question_calculations: z.array(InsightQuestionCalculations).optional(),
  location_groups: z.array(ChatSourcesLocationGroup).optional(),
  location_candidates: z.array(z.string()).optional(),
  sources: z.string().optional(),
  timeframe_groups: z.array(ChatSourcesTimeframeGroup).optional(),
  timeframe_candidates: z.array(z.string()).optional(),
  error: ApiError.optional()
});

export const AudiencesResponse = z.object({
  audiences: z.array(Audience).nullable()
});

export const AudienceResponse = z.object({
  audience: Audience
});

export const CalculationMetadata = z.object({
  Operation: z.string(),
  AudiencesCount: z.number(),
  QuestionsCount: z.number(),
  AttributesCount: z.number(),
  IntersectionsCount: z.number()
});

export const AudienceReportResponse = z.object({
  report: z.string()
});

export const Category = z.object({
  id: z.string(),
  name: z.string(),
  order: z.number()
});

export const CategoriesResponse = z.object({
  category_children: z.array(Category).optional()
});

export const RelevantAudience = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  type: z.nativeEnum(AudienceType)
});

export const RelevantAudiencesResponse = z.object({
  audiences: z.array(RelevantAudience),
  error: ApiError.optional()
});

export const DatasetAccessResponse = z.object({
  datasets: z.array(Dataset),
  error: ApiError.optional()
});

export const AudienceDetailsResponse = z.array(Audience);

export const ArtifactListItem = z.object({
  id: z.string(),
  title: z.string(),
  chat_id: z.string(),
  created_at: z.string(),
  updated_at: z.string()
});

export const ArtifactListResponse = z.array(ArtifactListItem);

export const ArtifactSpec = z.object({
  root: z.string(),
  elements: z.record(z.string(), z.unknown())
});

const artifactVersionFields = {
  artifact_id: z.string(),
  artifact_version_id: z.string(),
  version_number: z.number(),
  created_at: z.string()
};

export const ArtifactJsonVersion = z.object({
  ...artifactVersionFields,
  content_type: z.literal('json'),
  content: ArtifactSpec
});

export const ArtifactMarkdownVersion = z.object({
  ...artifactVersionFields,
  content_type: z.literal('markdown'),
  content: z.string()
});

export const ArtifactVersion = z.discriminatedUnion('content_type', [
  ArtifactJsonVersion,
  ArtifactMarkdownVersion
]);

export const ArtifactVersionSummary = z.object({
  ...artifactVersionFields,
  content_type: z.enum(['json', 'markdown'])
});

export const ArtifactDetailResponse = ArtifactListItem.extend({
  latest_versions: z.array(ArtifactVersion)
});

export const ArtifactVersionsResponse = z.array(ArtifactVersionSummary);
